import { FC, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import {
  IGNORE_KEY,
  MappingSector,
  categoryColor,
  saveSectorMapping,
  useSectorMapping,
} from '../../hooks/event-comparison'
import ModalShell from '../ModalShell'
import { SERIES_BASE, SERIES_COMPARE } from './styles'

const INK = '#212121'
const MUTED = '#555555'
const FAINT = '#B0B0B0'
const BORDER = '#EAECF0'

const Config = styled.div`
  padding: 12px 20px;
  border-bottom: 1px solid ${BORDER};
  background: #fafbfc;
`
const ConfigHead = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${FAINT};
  margin-bottom: 8px;
`
const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`
const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 1px solid ${BORDER};
  background: #fff;
  border-radius: 999px;
  padding: 4px 8px 4px 10px;
`
const ChipInput = styled.input`
  border: 0;
  outline: none;
  font-size: 12.5px;
  font-weight: 600;
  color: ${INK};
  width: 108px;
  background: transparent;
`
const ChipX = styled.button`
  border: 0;
  background: transparent;
  color: ${FAINT};
  cursor: pointer;
  font-size: 13px;
  line-height: 1;
  padding: 0 2px;
  &:hover { color: #b42318; }
`
const AddBtn = styled.button`
  border: 1px dashed ${BORDER};
  background: #fff;
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #B0020B;
  cursor: pointer;
`
const Dot = styled.span<{ $c: string }>`
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background: ${(p) => p.$c};
  flex: none;
`
const Cols = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`
const Col = styled.div`
  padding: 14px 20px;
  & + & { border-left: 1px solid ${BORDER}; }
  @media (max-width: 760px) {
    & + & { border-left: 0; border-top: 1px solid ${BORDER}; }
  }
`
const ColHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  font-weight: 660;
  color: ${INK};
  margin-bottom: 10px;
`
const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 9px 0;
  & + & { border-top: 1px solid #f2f4f7; }
`
const SName = styled.div`
  font-size: 13px;
  color: ${INK};
  font-weight: 560;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
const SMeta = styled.div`
  font-size: 11px;
  color: ${FAINT};
  font-variant-numeric: tabular-nums;
`
const SelWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`
const Select = styled.select`
  font-size: 12px;
  font-weight: 600;
  color: ${INK};
  border: 1px solid ${BORDER};
  border-radius: 8px;
  padding: 5px 8px;
  background: #fff;
  cursor: pointer;
  max-width: 140px;
`
const Note = styled.span`
  font-size: 11.5px;
  color: ${FAINT};
`
const Btn = styled.button<{ $primary?: boolean }>`
  font-size: 12.5px;
  font-weight: 640;
  border-radius: 9px;
  padding: 8px 16px;
  cursor: pointer;
  border: 1px solid ${(p) => (p.$primary ? SERIES_BASE : BORDER)};
  background: ${(p) => (p.$primary ? SERIES_BASE : 'transparent')};
  color: ${(p) => (p.$primary ? '#fff' : MUTED)};
  &:disabled { opacity: 0.6; cursor: default; }
`
const Empty = styled.div`
  font-size: 12.5px;
  color: ${FAINT};
  padding: 8px 0;
`

const fmtBRL = (n: number) => 'R$ ' + Math.round(n).toLocaleString('pt-BR')

interface Category {
  id: string
  label: string
}

interface Props {
  baseEventIds: number[]
  compareEventIds: number[]
  baseName: string
  compareName: string
  onClose: () => void
  onSaved: () => void
}

const EventColumn: FC<{
  name: string
  color: string
  sectors: MappingSector[]
  categories: Category[]
  sel: Record<string, string>
  onChange: (id: number, catId: string) => void
  loading: boolean
}> = ({ name, color, sectors, categories, sel, onChange, loading }) => (
  <Col>
    <ColHead><Dot $c={color} /> {name}</ColHead>
    {loading ? (
      <Empty>Carregando setores…</Empty>
    ) : sectors.length === 0 ? (
      <Empty>Evento sem setores.</Empty>
    ) : (
      sectors.map((s) => (
        <Row key={s.id}>
          <div style={{ minWidth: 0 }}>
            <SName title={s.name}>{s.name}</SName>
            <SMeta>{fmtBRL(s.unitPrice)} · {s.qtd.toLocaleString('pt-BR')} vendidos</SMeta>
          </div>
          <SelWrap>
            <Dot $c={categoryColor(
              sel[String(s.id)] === IGNORE_KEY
                ? IGNORE_KEY
                : categories.find((c) => c.id === sel[String(s.id)])?.label || ''
            )} />
            <Select value={sel[String(s.id)] || IGNORE_KEY} onChange={(e) => onChange(s.id, e.target.value)}>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.label || '(sem nome)'}</option>
              ))}
              <option value={IGNORE_KEY}>Ignorar</option>
            </Select>
          </SelWrap>
        </Row>
      ))
    )}
  </Col>
)

/** Tela "Revisar mapeamento de setores" (OP-3415) — categorias configuráveis
 *  (texto livre, renomeáveis) + auto-sugestão pelo nome do setor. Salva por evento. */
const SectorMappingModal: FC<Props> = ({
  baseEventIds,
  compareEventIds,
  baseName,
  compareName,
  onClose,
  onSaved,
}) => {
  const baseMap = useSectorMapping(baseEventIds)
  const compareMap = useSectorMapping(compareEventIds)

  const [categories, setCategories] = useState<Category[]>([])
  const [baseSel, setBaseSel] = useState<Record<string, string>>({})
  const [compareSel, setCompareSel] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const initedRef = useRef(false)
  const seqRef = useRef(0)

  const baseSectors = baseMap.mapping?.sectors
  const compareSectors = compareMap.mapping?.sectors

  // inicializa categorias e seleções a partir dos rótulos sugeridos/salvos
  useEffect(() => {
    if (initedRef.current || !baseSectors || !compareSectors) return
    initedRef.current = true

    const labels = Array.from(
      new Set(
        [...baseSectors, ...compareSectors]
          .map((s) => s.category)
          .filter((c) => c && c !== IGNORE_KEY)
      )
    )
    const cats: Category[] = labels.map((label) => ({ id: `c${seqRef.current++}`, label }))
    const idByLabel = new Map(cats.map((c) => [c.label, c.id]))
    const selOf = (sectors: MappingSector[]) =>
      Object.fromEntries(
        sectors.map((s) => [
          String(s.id),
          s.category === IGNORE_KEY ? IGNORE_KEY : idByLabel.get(s.category) || IGNORE_KEY,
        ])
      )
    setCategories(cats)
    setBaseSel(selOf(baseSectors))
    setCompareSel(selOf(compareSectors))
  }, [baseSectors, compareSectors])

  const loading = baseMap.loading || compareMap.loading

  const renameCategory = (id: string, label: string) =>
    setCategories((cs) => cs.map((c) => (c.id === id ? { ...c, label } : c)))
  const addCategory = () =>
    setCategories((cs) => [...cs, { id: `c${seqRef.current++}`, label: 'Nova categoria' }])
  const removeCategory = (id: string) => {
    setCategories((cs) => cs.filter((c) => c.id !== id))
    const drop = (sel: Record<string, string>) =>
      Object.fromEntries(Object.entries(sel).map(([k, v]) => [k, v === id ? IGNORE_KEY : v]))
    setBaseSel(drop)
    setCompareSel(drop)
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    const labelOf = (catId: string) =>
      catId === IGNORE_KEY ? IGNORE_KEY : categories.find((c) => c.id === catId)?.label.trim() || IGNORE_KEY
    const toMapping = (sel: Record<string, string>) =>
      Object.fromEntries(Object.entries(sel).map(([sid, catId]) => [sid, labelOf(catId)]))
    try {
      await Promise.all([
        saveSectorMapping(baseEventIds, toMapping(baseSel)),
        saveSectorMapping(compareEventIds, toMapping(compareSel)),
      ])
      onSaved()
    } catch (e) {
      setError('Não foi possível salvar. Tente novamente.')
    } finally {
      setSaving(false)
    }
  }

  const emptyLabel = useMemo(() => categories.some((c) => !c.label.trim()), [categories])

  const footer = (
    <>
      <Note>
        {error ? (
          <span style={{ color: '#B42318' }}>{error}</span>
        ) : (
          'Setores marcados como "Ignorar" ficam fora da comparação.'
        )}
      </Note>
      <Btn onClick={onClose} disabled={saving}>Cancelar</Btn>
      <Btn $primary onClick={handleSave} disabled={saving || emptyLabel}>
        {saving ? 'Salvando…' : 'Salvar mapeamento'}
      </Btn>
    </>
  )

  return (
    <ModalShell
      title="Revisar mapeamento de setores"
      subtitle="Categorias sugeridas pelo nome do setor (2 primeiras palavras). Renomeie, crie ou ajuste — setores com a mesma categoria são comparados juntos. Salvo por evento."
      onClose={onClose}
      footer={footer}
    >
      <Config>
          <ConfigHead>Categorias — clique para renomear</ConfigHead>
          <Chips>
            {categories.map((c) => (
              <Chip key={c.id}>
                <Dot $c={categoryColor(c.label)} />
                <ChipInput
                  value={c.label}
                  onChange={(e) => renameCategory(c.id, e.target.value)}
                  placeholder="nome"
                />
                <ChipX onClick={() => removeCategory(c.id)} title="Remover">✕</ChipX>
              </Chip>
            ))}
            <AddBtn type="button" onClick={addCategory}>＋ Adicionar categoria</AddBtn>
          </Chips>
        </Config>

        <Cols>
          <EventColumn
            name={baseName}
            color={SERIES_BASE}
            sectors={baseSectors || []}
            categories={categories}
            sel={baseSel}
            onChange={(id, catId) => setBaseSel((m) => ({ ...m, [String(id)]: catId }))}
            loading={loading}
          />
          <EventColumn
            name={compareName}
            color={SERIES_COMPARE}
            sectors={compareSectors || []}
            categories={categories}
            sel={compareSel}
            onChange={(id, catId) => setCompareSel((m) => ({ ...m, [String(id)]: catId }))}
            loading={loading}
          />
        </Cols>
    </ModalShell>
  )
}

export default SectorMappingModal
